import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Link,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import messages from './messages';
import muaMessages from '../../ManageUnitAvailabilityV2/messages';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useIntl from '../../../hooks/useIntl';

type Props = {
  areApplicationFeesPaid: boolean,
  hasAssignedUnit: boolean,
  routeToAvailableUnits: Function,
  unassignUnit: Function,
  propertyConfigs: Object,
  isDisabled: boolean,
  applicationStatusName?: string,
  canAssignUnitBeforeFee?: boolean,
  canAssignUnassignUnit?: boolean,
};

const ASSIGNABLE_APPLICATION_STATUSES = [
  'Approved',
  'In Process',
  'Converted to Resident',
];

const AssignUnitLink = ({
  areApplicationFeesPaid,
  hasAssignedUnit,
  routeToAvailableUnits,
  unassignUnit,
  propertyConfigs,
  isDisabled,
  applicationStatusName,
  canAssignUnitBeforeFee,
  canAssignUnassignUnit,
}: Props) => {
  const { assigningUnitsAtApplication } = useFlags();
  const intl = useIntl();
  const isUnitSelectionEnabled = assigningUnitsAtApplication
    ? propertyConfigs?.isUnitSelectionEnabled ?? false
    : false;

  const { onClick, message, disabled, tooltipMessage } = useMemo(() => {
    const linkProps = {
      onClick: () => {},
      message: {},
      disabled: false,
      tooltipMessage: null,
    };

    if (hasAssignedUnit) {
      linkProps.onClick = unassignUnit;
      linkProps.message = messages.unAssignUnit;

      if (!canAssignUnassignUnit || isDisabled) {
        linkProps.disabled = true;
        linkProps.tooltipMessage = !canAssignUnassignUnit
          ? intl.formatMessage(messages.assignUnassignUnitDisabled)
          : intl.formatMessage(messages.unassignDisabled);
      }
    } else {
      linkProps.message = messages.assignUnit;

      if (!canAssignUnassignUnit) {
        linkProps.disabled = true;
        linkProps.tooltipMessage = intl.formatMessage(
          messages.assignUnassignUnitDisabled,
        );
      } else if (
        !ASSIGNABLE_APPLICATION_STATUSES.includes(applicationStatusName)
      ) {
        linkProps.disabled = true;
        linkProps.tooltipMessage = intl.formatMessage(
          muaMessages.invalidApplicationStatusTooltip,
        );
      } else if (isUnitSelectionEnabled) {
        linkProps.disabled = !(
          areApplicationFeesPaid || canAssignUnitBeforeFee
        );
        linkProps.onClick = () => {
          const useOverride = canAssignUnitBeforeFee && !areApplicationFeesPaid;
          routeToAvailableUnits(useOverride ? { isOverride: true } : {});
        };

        if (linkProps.disabled) {
          linkProps.tooltipMessage = intl.formatMessage(
            messages.assignUnitBeforeFeeDisabled,
          );
        }
      } else {
        linkProps.disabled = false;
        linkProps.onClick = () => routeToAvailableUnits();
      }
    }

    return linkProps;
  }, [
    hasAssignedUnit,
    unassignUnit,
    isDisabled,
    applicationStatusName,
    routeToAvailableUnits,
    isUnitSelectionEnabled,
    areApplicationFeesPaid,
    canAssignUnitBeforeFee,
    canAssignUnassignUnit,
  ]);

  return (
    <Tooltip component={'span'} title={tooltipMessage}>
      <Link onClick={onClick} disabled={disabled}>
        <FormattedMessage {...message} />
      </Link>
    </Tooltip>
  );
};

AssignUnitLink.displayName = 'AssignUnitLink';

export default AssignUnitLink;
